import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Svg, Typography } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared';
import { Dialog } from '@/_shared/components/Dialog';
import { Form } from '@/_shared/components/Form';
import { useNotification } from '@/_shared/hooks/useNotification';
import { getSurveysCampaignsCall, putSurveyCampaignVisibilityCall } from '../../state';

type Props = {
  currentCampaignId?: string;
  isOpen: boolean;
  closeDialog: () => void;
  isVisible?: boolean;
  totalCompletedResponses?: number;
  setIsCompleted?: (value: boolean) => void;
  setTrigger?: (value: boolean) => void;
};

export const SetVisibilityDialog = ({
  isOpen,
  closeDialog,
  isVisible,
  currentCampaignId,
  totalCompletedResponses = 0,
  setIsCompleted,
  setTrigger,
}: Props) => {
  const t = useTranslations('surveys.campaign.setVisibilityDialog');
  const sharedT = useTranslations();

  const [internalVisibility, setInternalVisibility] = useState(isVisible);

  const { notify } = useNotification();

  const {
    execute: executePutSurveyCampaignVisibility,
    loading: loadingPutSurveyCampaignVisibility,
  } = putSurveyCampaignVisibilityCall.hook();

  const { execute: executeGetSurveysCampaigns } = getSurveysCampaignsCall.hook();

  const { control } = useForm();

  const onConfirm = useCallback(() => {
    setIsCompleted?.(false);

    if (currentCampaignId && internalVisibility !== undefined) {
      executePutSurveyCampaignVisibility({
        id: currentCampaignId,
        isVisible: internalVisibility,
        successSaga: () => {
          if (setTrigger) {
            setTrigger(false);
          }
          executeGetSurveysCampaigns({});
          notify(sharedT('success.genericApiSuccess'), 'success');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        },
        errorSaga: () => {
          notify(sharedT('errors.genericApiError'), 'error');
        },
      });
    }
  }, [
    currentCampaignId,
    executeGetSurveysCampaigns,
    executePutSurveyCampaignVisibility,
    internalVisibility,
    notify,
    setIsCompleted,
    setTrigger,
    sharedT,
  ]);

  return (
    <Dialog
      maxWidth='700px'
      name='reminderSurvey'
      open={isOpen}
      title={undefined}
      onClose={closeDialog}
      closeButton
    >
      <Box display='flex' flexDirection='column'>
        <Typography mt='none' variant='h3' color='primary/text' textAlign='center'>
          {t('title')}
        </Typography>
      </Box>

      <Box mb='xs' mt='m' width='100%'>
        <Typography variant='p2/light' mb='xs' color='primary/text' textAlign='center'>
          {t('description')}
        </Typography>
      </Box>

      <Box
        pr='xxs'
        mb='xl'
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        width='100%'
      >
        <Typography variant='p2/label'>{t('labelToggle')}</Typography>
        <Box display='flex' alignItems='center'>
          <Typography variant='p3/light' mr={16}>
            {internalVisibility ? t('valueOnToggle') : t('valueOffToggle')}
          </Typography>
          <Form.FormToggle
            onClick={() => setInternalVisibility(prev => !prev)}
            control={control}
            name='visibility'
            isChecked={internalVisibility}
          />
        </Box>
      </Box>

      <AlertBadge
        type='primary'
        iconName='lightBulb'
        label={
          // eslint-disable-next-line no-nested-ternary
          isVisible
            ? totalCompletedResponses === 0
              ? t('alert.visible')
              : t('alert.visibleWithResponses', {
                  totalCompletedResponses: `${totalCompletedResponses} ${
                    totalCompletedResponses === 1 ? t('person') : t('persons')
                  }`,
                })
            : t('alert.hidden')
        }
      />

      <Box mt='l' display='flex' flexDirection='row'>
        <Box mr='s' alignItems='center' alignContent='center' display='flex' flexDirection='row'>
          <Svg name='close' size='s' />
          <Button height='none' variant='link/primary' onClick={closeDialog}>
            {t('buttons.cancel')}
          </Button>
        </Box>
        <Button
          variant='primary'
          spinnerColor='white'
          loading={loadingPutSurveyCampaignVisibility}
          onClick={onConfirm}
          disabled={internalVisibility === isVisible}
        >
          {t('buttons.save')}
        </Button>
      </Box>
    </Dialog>
  );
};
